import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  MultiSelectSearch,
} from "@/components/ui";
import { useSnomedSearch } from "@/network/useOntologies";
import { type Observation, getDisplay } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { SubmitCancelButtons } from "./SubmitCancelButtons";
import type {
  GenericObservationDisplayProps,
  GenericObservationFormProps,
} from "./types";

const formSchema = z.object({
  valueCodes: z
    .array(
      z.object({
        code: z.string().min(1, "Code is required"),
        display: z.string().min(1, "Display is required"),
        system: z.string().min(1, "System is required"),
      }),
    )
    .min(1, "At least one code is required"),
});

const getValueCodes = (observation?: Pick<Observation, "component"> | null) => {
  const valueCodes = [];
  for (const component of observation?.component || []) {
    if (component.valueCodeableConcept) {
      valueCodes.push({
        // HACK: note that in this case, we are ok hardcoding the 0th index as
        // the code systems for generic observations are very unlikely to change
        code: component.valueCodeableConcept?.coding?.[0]?.code,
        display: getDisplay(component.valueCodeableConcept),
        system: component.valueCodeableConcept?.coding?.[0]?.system,
      });
    }
  }
  return valueCodes;
};

export const GenericObservationMultiCodeForm = ({
  patientId,
  item,
  itemCategory,
  onSubmit,
  onCancel,
  isSubmitting,
}: GenericObservationFormProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const eclExpression = item.parentCodes
    ? `<< (${item.parentCodes.join(" OR ")})`
    : `<< ${item.parentCode}`;
  const { data: codes, isLoading: isLoadingCodes } = useSnomedSearch({
    enabled: Boolean(item.parentCode || item.parentCodes),
    preloadOptions: item.preloadOptions,
    searchTerm,
    eclExpression,
  });
  // if we have *both* parent codes within which we search _and_ hardcoded observation values,
  // append the hardcoded observation values to any search results received when searching
  // within the parent codes
  const allCodes = (codes || [])?.concat(
    item.observationValues.map((value) => ({
      code: value.value,
      display: value.display,
      system: value.system,
    })),
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      valueCodes: getValueCodes(item.documentedObservation),
    },
  });
  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    const fhirValues = data.valueCodes.map((valueCode) => ({
      valueCodeableConcept: {
        coding: [
          {
            code: valueCode.code,
            system: valueCode.system,
            display: valueCode.display,
          },
        ],
        text: valueCode.display,
      },
    }));
    const payload = {
      patientId: patientId as string,
      category: itemCategory,
      status: "final",
      code: {
        coding: [
          {
            code: item.observationCode,
            system: item.observationCodeSystem,
            display: item.observationCodeDisplay,
          },
        ],
        text: item.observationCodeFriendlyDisplay,
      },
      fhirValues,
    };
    onSubmit(payload);
  };
  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col gap-2">
        <Controller
          control={form.control}
          name="valueCodes"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="valueCodes">
                <BodyText size="sm" bold>
                  {item.observationCodeFriendlyDisplay}
                </BodyText>
              </FieldLabel>
              <MultiSelectSearch
                {...field}
                autoFocusInput
                placeholder="Search for codes"
                isLoading={
                  item.parentCode || item.parentCodes ? isLoadingCodes : false
                }
                onInputChange={(value) => setSearchTerm(value)}
                options={
                  (item.parentCode || item.parentCodes
                    ? allCodes?.map((code) => ({
                        label: code.display,
                        value: code.code,
                        metadata: {
                          system: code.system,
                        },
                      }))
                    : item.observationValues?.map((code) => ({
                        label: code.display,
                        value: code.value,
                        metadata: {
                          system: code.system,
                        },
                      }))) || []
                }
                selectedItems={field.value.map((code) => ({
                  label: code.display,
                  value: code.code,
                  metadata: {
                    system: code.system,
                  },
                }))}
                onItemAdded={(item) => {
                  form.setValue("valueCodes", [
                    ...field.value,
                    {
                      code: item.value as string,
                      display: item.label,
                      system:
                        (item.metadata as { system?: string })?.system || "",
                    },
                  ]);
                }}
                shouldFilter={
                  Boolean(item.observationValues) &&
                  Boolean(item.parentCode || item.parentCodes)
                }
                onItemRemoved={(item) => {
                  form.setValue(
                    "valueCodes",
                    [...field.value].filter(
                      (valueCode) => valueCode.code !== item.value,
                    ),
                  );
                }}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
        <SubmitCancelButtons isSaving={isSubmitting} onCancel={onCancel} />
      </div>
    </form>
  );
};

export const GenericObservationMultiCodeDisplay = ({
  item,
}: GenericObservationDisplayProps) => {
  const valueCodes = getValueCodes(item.documentedObservation);
  return (
    <BodyText size="sm">
      {valueCodes.length > 0
        ? valueCodes.map((valueCode) => valueCode.display).join(", ")
        : "Unknown"}
    </BodyText>
  );
};
